import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBuildingState } from "../../../store/actions/permitFormActions/buildingStateActions";
import { privateProviderLabel } from "../../../data/buildingPermit/fieldDatas";
import { fieldsToCheckPrivateProvider } from "../../../data/permitFormsData";
import { formikRef } from "../../../store/formikStore/formikStore";
import { LoadingButton } from "@mui/lab";
import {
  handleSaveProjects,
  pushNewPrivateProvider,
} from "../../../store/actions/dataActions";
import {
  TEMPLATE_CATEGORY_IDS,
  getTemplateCategoryConfig,
} from "../../permitForms/utils/templateCategoryHelpers";

const getFieldName = (field) => field?.name || field?.field_name;

const normalizeFieldName = (name = "") =>
  String(name)
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const getComparableFieldNames = (name, config) => {
  if (!name) return new Set();

  const addressBaseName = String(name).replace(/_address_street$/, "_address");
  const directNames = [
    name,
    addressBaseName,
    ...(config?.aliases?.[name] || []),
    ...(config?.aliases?.[addressBaseName] || []),
  ];
  const reverseAliasNames = Object.entries(config?.aliases || {})
    .filter(([key, aliases]) =>
      [key, ...(aliases || [])].some((alias) =>
        directNames.some(
          (candidate) =>
            normalizeFieldName(candidate) === normalizeFieldName(alias),
        ),
      ),
    )
    .flatMap(([key, aliases]) => [key, ...(aliases || [])]);

  return new Set(
    [...directNames, ...reverseAliasNames]
      .map(normalizeFieldName)
      .filter(Boolean),
  );
};

const areEquivalentFields = (dynamicName, staticName, config) => {
  const dynamicNames = getComparableFieldNames(dynamicName, config);
  const staticNames = getComparableFieldNames(staticName, config);

  return [...dynamicNames].some((name) => staticNames.has(name));
};

const getFieldLabel = (field) => {
  const name = getFieldName(field);
  return (
    field?.label ||
    field?.field_label ||
    field?.title ||
    privateProviderLabel[name] ||
    String(name || "")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase())
  );
};

const AddingNewPrivateProviderModal = () => {
  const formik = formikRef.current;
  const dispatch = useDispatch();

  const {
    addingNewPrivateProvider,
    privateProviderLoading,
    activeTemplateCategory,
  } = useSelector((state) => state.buildingState);

  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [fieldValues, setFieldValues] = useState({
    pp_company_name: "",
    pp_address: "",
    pp_phone: "",
    pp_email: "",
    pp_name: "",
    pp_license: "",
  });

  const dynamicCategoryFields = useMemo(() => {
    const dynamicFields =
      activeTemplateCategory?.categoryId ===
      TEMPLATE_CATEGORY_IDS.PRIVATE_PROVIDER
        ? activeTemplateCategory.fields || []
        : [];
    const fieldsById = new Map();

    dynamicFields.forEach((field) => {
      const name = getFieldName(field);
      const identity = field?.field_id || name;
      if (identity && name && !fieldsById.has(identity)) {
        fieldsById.set(identity, field);
      }
    });

    return Array.from(fieldsById.values());
  }, [activeTemplateCategory]);

  const resolvedFields = useMemo(() => {
    if (!dynamicCategoryFields.length) {
      return fieldsToCheckPrivateProvider.map((name) => ({ name }));
    }

    const config = getTemplateCategoryConfig(
      TEMPLATE_CATEGORY_IDS.PRIVATE_PROVIDER,
    );
    const missingStaticFields = fieldsToCheckPrivateProvider
      .filter(
        (staticName) =>
          !dynamicCategoryFields.some((field) =>
            areEquivalentFields(getFieldName(field), staticName, config),
          ),
      )
      .map((name) => ({ name }));

    return [...dynamicCategoryFields, ...missingStaticFields];
  }, [dynamicCategoryFields]);

  const resolvedFieldNames = useMemo(
    () => resolvedFields.map(getFieldName).filter(Boolean),
    [resolvedFields],
  );

  const requiredCompanyFieldName = useMemo(() => {
    if (!dynamicCategoryFields.length) return "pp_company_name";

    const config = getTemplateCategoryConfig(
      TEMPLATE_CATEGORY_IDS.PRIVATE_PROVIDER,
    );
    const matchingField = dynamicCategoryFields.find((field) => {
      const dynamicName = getFieldName(field);
      return areEquivalentFields(dynamicName, "pp_company_name", config);
    });

    return getFieldName(matchingField) || "pp_company_name";
  }, [dynamicCategoryFields]);

  useEffect(() => {
    if (!addingNewPrivateProvider || !resolvedFieldNames.length) return;

    setFieldValues((prev) => ({
      ...prev,
      ...resolvedFieldNames.reduce(
        (values, name) => ({ ...values, [name]: prev[name] ?? "" }),
        {},
      ),
    }));
  }, [addingNewPrivateProvider, resolvedFieldNames]);

  useEffect(() => {
    const callFunc = () => {
      let error = false;

      // Check required fields - at least company name should be filled
      const companyName = requiredCompanyFieldName
        ? String(fieldValues[requiredCompanyFieldName] ?? "")
        : "";

      if (!companyName || companyName.trim().length < 3) {
        error = true;
      }

      setSubmitDisabled(error);
    };

    callFunc();
  }, [fieldValues, requiredCompanyFieldName]);

  const runFieldChange = (event) => {
    const { name, value } = event.target;

    setFieldValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddNew = async () => {
    try {
      await dispatch(setBuildingState({ privateProviderLoading: true }));

      const newID = Date.now().toString();
      const createdAt = Math.floor(Date.now() / 1000);

      const categoryFieldValues = resolvedFieldNames.reduce(
        (values, name) => ({
          ...values,
          [name]: fieldValues[name] ?? "",
        }),
        {},
      );
      const fieldValuesById = resolvedFields.reduce((values, field) => {
        const fieldId = field?.field_id;
        const fieldName = getFieldName(field);

        return fieldId && fieldName
          ? { ...values, [fieldId]: fieldValues[fieldName] ?? "" }
          : values;
      }, {});

      const savedProjectObj = {
        id: newID,
        ...categoryFieldValues,
        ...(Object.keys(fieldValuesById).length
          ? {
              category_id: TEMPLATE_CATEGORY_IDS.PRIVATE_PROVIDER,
              field_values: fieldValuesById,
            }
          : {}),
        created_at: createdAt,
      };

      await dispatch(pushNewPrivateProvider(savedProjectObj));

      await dispatch(handleSaveProjects());
      formik?.setValues((prevValues) => ({
        ...prevValues,
        privateProvider: newID,
        ...categoryFieldValues,
      }));

      dispatch(
        setBuildingState({
          addingNewPrivateProvider: false,
          activeTemplateCategory: null,
        }),
      );
      // Reset field values
      setFieldValues({
        pp_company_name: "",
        pp_address: "",
        pp_phone: "",
        pp_email: "",
        pp_name: "",
        pp_license: "",
      });
    } catch (e) {
      console.error("Error adding private provider:", e);
    } finally {
      await dispatch(setBuildingState({ privateProviderLoading: false }));
    }
  };

  const handlePhoneInputChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    if (value.length > 10) value = value.slice(0, 10);

    let formatted = "";
    if (value.length > 0) {
      if (value.length <= 3) {
        formatted = value;
      } else if (value.length <= 6) {
        formatted = `${value.slice(0, 3)}-${value.slice(3)}`;
      } else {
        formatted = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
      }
    }

    setFieldValues((prev) => ({
      ...prev,
      pp_phone: formatted,
    }));
  };

  return (
    <Modal
      open={addingNewPrivateProvider}
      onClose={() => {
        dispatch(
          setBuildingState({
            addingNewPrivateProvider: false,
            activeTemplateCategory: null,
          }),
        );
        setFieldValues({
          pp_company_name: "",
          pp_address: "",
          pp_phone: "",
          pp_email: "",
          pp_name: "",
          pp_license: "",
        });
      }}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 500 },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <Typography id="modal-title" variant="h6" component="h2" mb={2}>
          Add New Private Provider
        </Typography>

        {resolvedFields.map((fieldConfig, i) => {
          const field = getFieldName(fieldConfig);

          return (
            <TextField
              key={field + i}
              fullWidth
              margin="normal"
              variant="filled"
              label={getFieldLabel(fieldConfig)}
              name={field}
              value={fieldValues[field] ?? ""}
              onChange={
                field === "pp_phone" ? handlePhoneInputChange : runFieldChange
              }
              required={field === requiredCompanyFieldName}
              error={
                field === requiredCompanyFieldName &&
                fieldValues[field] &&
                String(fieldValues[field]).trim().length < 3
              }
              helperText={
                field === requiredCompanyFieldName &&
                fieldValues[field] &&
                String(fieldValues[field]).trim().length < 3
                  ? "Firm Name must be at least 3 characters"
                  : ""
              }
            />
          );
        })}

        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Button
            onClick={() => {
              dispatch(
                setBuildingState({
                  addingNewPrivateProvider: false,
                  activeTemplateCategory: null,
                }),
              );
              setFieldValues({
                pp_company_name: "",
                pp_address: "",
                pp_phone: "",
                pp_email: "",
                pp_name: "",
                pp_license: "",
              });
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={privateProviderLoading}
            variant="contained"
            onClick={handleAddNew}
            disabled={submitDisabled}
          >
            Save
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(AddingNewPrivateProviderModal);
